import FieldControl, {FieldControlProps} from "./control";
import FieldSelect, {FieldSelectProps} from "./select";
import FieldTextField, {FieldTextFieldInputTypes, FieldTextFieldProps, FieldTextFieldValue} from "./text-field";
import FieldToggleButtons, {FieldToggleButtonsProps} from "./toggle-buttons";
import FieldMap, {FieldMapProps, FieldMapValue} from "./map";
import FieldAutocomplete, {FieldAutocompleteProps} from "./autocomplete";
import FieldDateTimePicker, {FieldDatePickerValue, FieldDateTimePickerProps} from "./date-picker";

export type FieldProps<Props> = Omit<Props, 'Field'> & {
    autoCommit?: boolean;
}

/**
 * Wrapper component for the Field components.
 */
const Field = {

    /**
     * The TextField component of the Field component.
     *
     * @template    InputType       The input type of the field.
     * @template    Readonly        Whether the field is readonly.
     * @param       props           The props for the FieldControl component.
     */
    TextField: <
        InputType extends FieldTextFieldInputTypes,
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<FieldTextFieldValue<InputType>, Readonly, FieldTextFieldProps<InputType>>>) => (
        <FieldControl {...props} Field={FieldTextField}/>
    ),

    /**
     * The Select component of the Field component.
     *
     * @template    Value           The value type of the field.
     * @template    Multiple        Whether the field is multiple.
     * @template    Readonly        Whether the field is readonly.
     * @param       props           The props for the FieldControl component.
     */
    Select: <
        Value,
        Multiple extends boolean = false,
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<Value, Readonly, FieldSelectProps<Value, Multiple>>>) => (
        <FieldControl {...props as any} Field={FieldSelect}/>
    ),

    /**
     * The ToggleButtons component of the Field component.
     *
     * @template    Value           The value type of the field.
     * @template    Exclusive       Whether the field is exclusive.
     * @param       props           The props for the FieldControl component.
     */
    ToggleButtons: <
        Value,
        Exclusive extends boolean = true,
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<Value, Readonly, FieldToggleButtonsProps<Value, Exclusive>>>) => (
        <FieldControl {...props} Field={FieldToggleButtons}/>
    ),

    /**
     * The Map component of the Field component.
     *
     * @template    Readonly        Whether the field is readonly.
     * @param       props           The props for the FieldControl component.
     */
    Map: <
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<FieldMapValue, Readonly, FieldMapProps>>) => (
        <FieldControl {...props} map Field={FieldMap}/>
    ),

    /**
     * The Autocomplete component of the Field component.
     *
     * @template    Value           The value type of the field.
     * @template    Multiple        Whether the field is multiple.
     * @template    FetchOptions    Whether the field fetches options.
     * @template    Readonly        Whether the field is readonly.
     * @param       props           The props for the FieldControl component.
     */
    Autocomplete: <
        Value,
        Multiple extends boolean = false,
        FetchOptions extends boolean = false,
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<Value, Readonly, FieldAutocompleteProps<Value, Multiple, FetchOptions>>>) => (
        <FieldControl {...props as any} Field={FieldAutocomplete}/>
    ),

    /**
     * The DateTimePicker component of the Field component.
     *
     * @template    Readonly        Whether the field is readonly.
     * @param       props           The props for the FieldControl component.
     */
    DateTimePicker: <
        Readonly extends boolean = boolean,
    >(props: FieldProps<FieldControlProps<FieldDatePickerValue, Readonly, FieldDateTimePickerProps>>) => (
        <FieldControl {...props} Field={FieldDateTimePicker}/>
    ),
    
}

export * from "./control";
export * from "./select";
export * from "./text-field";
export * from './autocomplete';
export * from './toggle-buttons';
export * from './map';

export default Field;
